'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
// import { calculateQuote } from '@/lib/quote-calculator';
import type { QuoteFormData } from '@/types/ai-quote';

export default function QuoteWizard() {
  const router = useRouter();

  const [formData, setFormData] = useState<QuoteFormData>({
    project_type: '',
    service_status: '',
    budget_range: '',
    schedule_target: '',
    project_one_line: '',
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [history, setHistory] = useState<Array<{ role: 'system' | 'user'; text: string }>>([]);
  const [tempMulti, setTempMulti] = useState<string[]>([]);
  const [tempSingle, setTempSingle] = useState<string>('');
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const projectTypes = [
    '유지보수/수리만 필요해요',
    '신규 웹 개발을 하고 싶어요',
    '신규 앱(모바일) 개발을 하고 싶어요',
    '웹 + 앱을 함께 개발하고 싶어요',
    '기존 서비스를 리뉴얼하고 싶어요',
  ];

  const serviceStatuses = [
    '아직 아무것도 없는 상태예요(기획 단계)',
    '디자인은 어느 정도 있어요',
    '이미 운영 중인 서비스가 있어요',
    '다른 개발사와 진행 중인데, 유지보수 업체를 바꾸고 싶어요',
  ];

  const budgetRanges = [
    '300만 원 미만',
    '300만 ~ 700만 원',
    '700만 ~ 1,500만 원',
    '1,500만 ~ 3,000만 원',
    '3,000만 원 이상 / 아직 잘 모르겠어요',
  ];

  const scheduleTargets = [
    '1~2개월 안에 런칭하고 싶어요',
    '3~4개월 안에 준비하고 싶어요',
    '6개월 이상 여유가 있어요',
    '일정은 유동적이에요',
  ];

  const maintenanceServiceTypes = ['웹사이트', '모바일 앱', '웹 + 앱', '관리자 페이지'];
  const techStacks = ['잘 모르겠어요', '워드프레스', '쇼핑몰 솔루션(카페24/고도몰 등)', 'Flutter', 'React / Next.js', 'Node.js 백엔드', '기타'];
  const issueTypes = [
    '자꾸 서버가 멈추거나 에러가 납니다',
    '속도가 너무 느립니다',
    '결제가 자주 실패합니다',
    '로그인/회원 관련 오류가 있습니다',
    '푸시 알림이 동작하지 않습니다',
    '보안/해킹이 걱정됩니다',
    '새로운 기능을 조금씩 추가하고 싶습니다',
    '전체적인 구조 점검이 필요합니다',
  ];
  const requestFrequencies = ['한 달에 1~2회 정도', '한 달에 3~5회 정도', '매주 여러 건 발생', '아직 잘 모르겠습니다'];
  const slaLevels = ['평일 근무 시간 내 대응이면 괜찮아요', '평일 야간도 어느 정도 대응되면 좋겠어요', '주말/야간 포함 긴급 대응이 필요합니다'];

  const newServiceTypes = ['웹 서비스', 'iOS 앱', 'Android 앱', '관리자 페이지', '랜딩 페이지'];
  const featureModules = [
    '모르겠어요',
    '회원가입/로그인',
    '소셜 로그인(카카오/네이버/구글 등)',
    '게시판/커뮤니티/댓글',
    '1:1 채팅 / 그룹 채팅',
    '푸시 알림',
    '결제(국내 PG/인앱결제)',
    '예약/일정 관리',
    '지도/위치 기반 기능',
    '파일/이미지 업로드',
    '통계/대시보드',
    '관리자 페이지(회원/콘텐츠 관리)'
  ];
  const designStatuses = ['디자인 처음부터 필요해요', '대략적인 와이어프레임/기획안은 있어요', '완성된 디자인 시안(Figma/PSD 등)이 있어요'];
  const integrationTargets = ['소셜 로그인', '결제(PG사, 인앱 결제 등)', '사내 기존 시스템(ERP/CRM/물류 등)', '외부 오픈 API(지도, 번역, AI 등)'];
  const priorities = ['개발 비용이 가장 중요해요', '일정(런칭 시점)이 가장 중요해요', '품질/완성도가 가장 중요해요', '비용/일정/품질 모두 균형이 필요해요'];

  type QType = 'single' | 'multi' | 'input';
  type Question = {
    id: keyof QuoteFormData;
    label: string;
    type: QType;
    options?: string[];
    visible?: boolean;
  };

  const filteredServiceTypes = useMemo(() => {
    if (formData.project_type === '신규 웹 개발을 하고 싶어요') return ['웹 서비스', '관리자 페이지', '랜딩 페이지'];
    if (formData.project_type === '신규 앱(모바일) 개발을 하고 싶어요') return ['iOS 앱', 'Android 앱'];
    if (formData.project_type === '웹 + 앱을 함께 개발하고 싶어요') return ['웹 서비스', 'iOS 앱', 'Android 앱', '관리자 페이지'];
    return newServiceTypes;
  }, [formData.project_type]);

  const questions = useMemo<Question[]>(() => {
    const isMaintenance = formData.project_type === '유지보수/수리만 필요해요';
    const isRenewal = formData.project_type === '기존 서비스를 리뉴얼하고 싶어요';

    const maintenanceBudgetRanges = [
      '20만 ~ 50만 원',
      '50만 ~ 100만 원',
      '100만 ~ 200만 원',
      '200만 ~ 300만 원',
      '300만 원 이상 / 아직 잘 모르겠어요',
    ];

    const maintenanceSchedules = [
      '1일 이내',
      '2~3일',
      '1주 이내',
      '2주 이내',
      '일정은 유동적이에요',
    ];

    const base: Question[] = [
      { id: 'project_type', label: '프로젝트 유형을 선택해주세요', type: 'single', options: projectTypes, visible: true },
      { id: 'service_status', label: '현재 상태를 선택해주세요', type: 'single', options: serviceStatuses, visible: true },
      { id: 'budget_range', label: '예상 예산 범위를 선택해주세요', type: 'single', options: isMaintenance ? maintenanceBudgetRanges : budgetRanges, visible: true },
      { id: 'schedule_target', label: '희망 일정을 선택해주세요', type: 'single', options: isMaintenance ? maintenanceSchedules : scheduleTargets, visible: true },
      { id: 'project_one_line', label: isMaintenance ? '수정할 내용을 한 문장으로 설명해주세요' : '프로젝트를 한 문장으로 설명해주세요', type: 'input', visible: true },
    ];

    const maint: Question[] = [
      { id: 'current_service_type', label: '현재 서비스 유형을 선택해주세요', type: 'single', options: maintenanceServiceTypes, visible: isMaintenance },
      { id: 'current_tech_stack', label: '현재 기술 스택을 선택해주세요', type: 'multi', options: techStacks, visible: isMaintenance },
      { id: 'maintenance_issue_types', label: '발생하는 이슈 유형을 선택해주세요', type: 'multi', options: issueTypes, visible: isMaintenance },
      { id: 'maintenance_request_frequency', label: '예상 요청 빈도를 선택해주세요', type: 'single', options: requestFrequencies, visible: isMaintenance },
      { id: 'sla_level', label: '필요한 대응 수준을 선택해주세요', type: 'single', options: slaLevels, visible: isMaintenance },
    ];

    const dev: Question[] = [
      { id: 'new_service_type', label: '개발하실 서비스 유형을 선택해주세요', type: 'multi', options: filteredServiceTypes, visible: !isRenewal && !isMaintenance },
      { id: 'feature_modules', label: '필요한 기능 모듈을 선택해주세요', type: 'multi', options: featureModules, visible: !isMaintenance },
      { id: 'design_status', label: '디자인 준비 상태를 선택해주세요', type: 'single', options: designStatuses, visible: !isRenewal && !isMaintenance },
      { id: 'integration_targets', label: '필요한 연동/통합을 선택해주세요', type: 'multi', options: integrationTargets, visible: !isMaintenance },
      { id: 'reference_website', label: '레퍼런스 웹사이트(URL)가 있다면 입력해주세요 (선택)', type: 'input', visible: !isMaintenance },
      { id: 'priority', label: '프로젝트 우선순위를 선택해주세요', type: 'single', options: priorities, visible: !isMaintenance },
    ];

    return [...base, ...maint, ...dev].filter(q => q.visible);
  }, [formData.project_type, filteredServiceTypes]);

  const total = questions.length;
  const progress = total > 0 ? Math.round((currentIndex / total) * 100) : 0;

  const updateFormData = (data: Partial<QuoteFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const currentQuestion = questions[currentIndex];

  // Typing indicator per question
  useEffect(() => {
    if (!currentQuestion) return;
    setIsTyping(true);
    const delay = 500 + Math.floor(Math.random() * 400);
    const t = setTimeout(() => {
      setHistory(prev => [...prev, { role: 'system', text: currentQuestion.label }]);
      setIsTyping(false);
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const commitAnswerAndNext = async () => {
    if (!currentQuestion) return;
    let answer: string | string[] = '';
    if (currentQuestion.type === 'single') {
      if (!tempSingle) return;
      updateFormData({ [currentQuestion.id]: tempSingle } as any);
      answer = tempSingle;
      setTempSingle('');
    } else if (currentQuestion.type === 'multi') {
      if (tempMulti.length === 0) return;
      updateFormData({ [currentQuestion.id]: tempMulti } as any);
      answer = tempMulti;
      setTempMulti([]);
    } else {
      const isOptional = currentQuestion.id === 'reference_website';
      const val = inputValue.trim();
      if (!val && !isOptional) return;
      updateFormData({ [currentQuestion.id]: val } as any);
      answer = val;
      setInputValue('');
    }

    const answerText = Array.isArray(answer) ? answer.join(', ') : answer;
    setHistory(prev => [...prev, { role: 'user', text: answerText }]);

    const nextIndex = currentIndex + 1;
    if (nextIndex >= total) {
      // Call our API to get OpenAI-based result
      const payload = { ...formData, [currentQuestion.id]: answer } as QuoteFormData;
      try {
        setSubmitting(true);
        console.log('[wizard] calling /api/ai-quote');
        const res = await fetch('/api/ai-quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const txt = await res.text();
          console.error('[wizard] /api/ai-quote failed', res.status, txt);
          throw new Error('AI quote failed');
        }
        const result = await res.json();
        console.log('[wizard] /api/ai-quote ok');
        try {
          localStorage.setItem('quoteResult', JSON.stringify(result));
          const existing = JSON.parse(localStorage.getItem('aiQuoteResults') || '[]');
          existing.push(result);
          localStorage.setItem('aiQuoteResults', JSON.stringify(existing));
        } catch (_) {}
        router.push('/ai-quote/result');
      } catch (e) {
        // Fallback to local calculator if API fails
        console.warn('[wizard] AI quote failed, fallback', e);
        // const result = calculateQuote(payload);
        // localStorage.setItem('quoteResult', JSON.stringify(result));
        // router.push('/ai-quote/result');
        alert('AI 견적 생성에 실패했습니다. 잠시 후 다시 시도해주세요.');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setCurrentIndex(nextIndex);
  };

  const toggleMulti = (val: string) => {
    setTempMulti(prev => (prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]));
  };

  // Edit feature removed

  const renderControls = () => {
    if (!currentQuestion) return null;
    if (currentQuestion.type === 'single') {
      return (
        <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:254:8" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="254" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20gap-2%22%7D" className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {currentQuestion.options?.map(opt => (
            <button data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:256:12" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="256" data-component-file="QuoteWizard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
              key={opt}
              type="button"
              onClick={() => setTempSingle(opt)}
              className={`p-3 border-2 rounded-lg text-left transition-all ${tempSingle === opt ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
            >
              {opt}
            </button>
          ))}
        </div>
      );
    }
    if (currentQuestion.type === 'multi') {
      return (
        <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:270:8" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="270" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
          <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:271:10" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="271" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20sm%3Agrid-cols-2%20gap-2%22%7D" className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {currentQuestion.options?.map(opt => {
              const selected = tempMulti.includes(opt);
              return (
                <button data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:275:16" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="275" data-component-file="QuoteWizard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
                  key={opt}
                  type="button"
                  onClick={() => toggleMulti(opt)}
                  className={`flex items-center gap-2 p-3 border-2 rounded-lg transition-all text-left ${selected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:281:18" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="281" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selected ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'}`}>
                    {selected && <Icon data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:282:33" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="282" data-component-file="QuoteWizard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%2C%22className%22%3A%22text-white%22%7D" name="CheckIcon" size={14} className="text-white" />}
                  </div>
                  <span data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:284:18" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="284" data-component-file="QuoteWizard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-medium%20text-sm%22%7D" className="font-medium text-sm">{opt}</span>
                </button>
              );
            })}
          </div>
          <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:289:10" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="289" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-end%22%7D" className="flex justify-end">
            <button data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:290:12" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="290" data-component-file="QuoteWizard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D" onClick={commitAnswerAndNext} className={`btn-primary ${(tempMulti.length === 0 || submitting) ? 'opacity-50 pointer-events-none' : ''}`}>{submitting ? '처리 중...' : '확인'}</button>
          </div>
        </div>
      );
    }
    const isMaintenance = formData.project_type === '유지보수/수리만 필요해요';
    const isRefWebsite = currentQuestion.id === 'reference_website';
    const oneLinePlaceholder = isRefWebsite
      ? '예: https://www.example.com'
      : isMaintenance
      ? '예: 결제 완료 후 주문 상태가 갱신되지 않습니다. 로그 수집 및 API 점검이 필요합니다.'
      : formData.project_type === '신규 앱(모바일) 개발을 하고 싶어요'
      ? '예: 동네 소모임 일정 공유 앱을 만들고 싶어요'
      : formData.project_type === '신규 웹 개발을 하고 싶어요'
      ? '예: 지역 맛집 리뷰 웹 서비스를 만들고 싶어요'
      : formData.project_type === '웹 + 앱을 함께 개발하고 싶어요'
      ? '예: 웹/앱이 연동되는 배달 플랫폼을 만들고 싶어요'
      : '예: 테니스 동호회 매칭 앱을 만들고 싶어요';
    return (
      <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:309:6" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="309" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
        <input data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:310:8" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="310" data-component-file="QuoteWizard.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22value%22%3A%22%5Bvar%3AinputValue%5D%22%2C%22className%22%3A%22input%20w-full%22%7D"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={oneLinePlaceholder}
          className="input w-full"
        />
        <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:316:8" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="316" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-end%22%7D" className="flex justify-end">
          <button data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:317:10" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="317" data-component-file="QuoteWizard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D" onClick={commitAnswerAndNext} className={`btn-primary ${((!inputValue.trim() && !isRefWebsite) || submitting) ? 'opacity-50 pointer-events-none' : ''}`}>{submitting ? '처리 중...' : '다음으로'}</button>
        </div>
      </div>
    );
  };

  return (
    <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:324:4" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="324" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-3xl%20mx-auto%22%7D" className="max-w-3xl mx-auto">
      <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:325:6" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="325" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
        <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:326:8" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="326" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-2%20w-full%20bg-gray-200%20rounded-full%20overflow-hidden%22%7D" className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
          <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:327:10" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="327" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-full%20bg-blue-600%20transition-all%22%7D" className="h-full bg-blue-600 transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:329:8" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="329" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%20text-right%20text-xs%20text-text-secondary%22%2C%22textContent%22%3A%22%2F%20%EC%A7%84%ED%96%89%22%7D" className="mt-2 text-right text-xs text-text-secondary">{currentIndex}/{total} 진행</div>
      </div>

      <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:332:6" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="332" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-lg%20shadow-sm%20border%20border-border%20p-6%22%7D" className="bg-white rounded-lg shadow-sm border border-border p-6">
        <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:333:8" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="333" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%20max-h-%5B60vh%5D%20overflow-auto%20pr-2%22%7D" className="space-y-3 max-h-[60vh] overflow-auto pr-2">
          {(() => {
            let sysCount = 0;
            let lastSystemIdx = -1;
            return history.map((m, idx) => {
              const isSystem = m.role !== 'user';
              if (isSystem) {
                lastSystemIdx = idx;
                sysCount += 1;
              }
              const qIdx = sysCount - 1;
              return (
                <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:345:16" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="345" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-1%22%7D" key={idx} className="space-y-1">
                  <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:346:18" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="346" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} transition-opacity duration-300`}>
                    <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:347:20" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="347" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-foreground'} px-4 py-2 rounded-2xl max-w-[80%]`}>{m.text}</div>
                  </div>
                </div>
              );
            });
          })()}

          {isTyping && (
            <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:355:12" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="355" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-3%20justify-start%22%7D" className="flex items-start gap-3 justify-start">
              <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:356:14" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="356" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20mt-0.5%22%7D" className="relative mt-0.5">
                <span data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:357:16" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="357" data-component-file="QuoteWizard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20inline-flex%20h-full%20w-full%20rounded-full%20bg-blue-400%20opacity-25%20animate-ping%22%7D" className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-25 animate-ping"></span>
                <span data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:358:16" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="358" data-component-file="QuoteWizard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22relative%20inline-flex%20h-7%20w-7%20rounded-full%20bg-blue-600%20flex%20items-center%20justify-center%20text-white%20text-xs%20font-semibold%22%2C%22textContent%22%3A%22AI%22%7D" className="relative inline-flex h-7 w-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-semibold">AI</span>
              </div>
              <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:360:14" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="360" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20py-2%20rounded-2xl%20max-w-%5B80%25%5D%20bg-gradient-to-r%20from-gray-100%20to-gray-200%20animate-pulse%22%7D" className="px-4 py-2 rounded-2xl max-w-[80%] bg-gradient-to-r from-gray-100 to-gray-200 animate-pulse">
                <span data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:361:16" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="361" data-component-file="QuoteWizard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-2%22%7D" className="inline-flex items-center gap-2">
                  <span data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:362:18" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="362" data-component-file="QuoteWizard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22w-2.5%20h-2.5%20rounded-full%20bg-gray-500%20animate-bounce%22%7D" className="w-2.5 h-2.5 rounded-full bg-gray-500 animate-bounce"></span>
                  <span data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:363:18" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="363" data-component-file="QuoteWizard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22w-2.5%20h-2.5%20rounded-full%20bg-gray-500%20animate-bounce%20%5Banimation-delay%3A120ms%5D%22%7D" className="w-2.5 h-2.5 rounded-full bg-gray-500 animate-bounce [animation-delay:120ms]"></span>
                  <span data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:364:18" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="364" data-component-file="QuoteWizard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22w-2.5%20h-2.5%20rounded-full%20bg-gray-500%20animate-bounce%20%5Banimation-delay%3A240ms%5D%22%7D" className="w-2.5 h-2.5 rounded-full bg-gray-500 animate-bounce [animation-delay:240ms]"></span>
                </span>
              </div>
            </div>
          )}
        </div>

        <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:371:8" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="371" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%22%7D" className="mt-6">
          {currentQuestion && !isTyping && currentQuestion.type === 'single' && (
            <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:373:12" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="373" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
              {renderControls()}
              <div data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:375:14" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="375" data-component-file="QuoteWizard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-end%22%7D" className="flex justify-end">
                <button data-component-id="src/app/ai-quote/wizard/components/QuoteWizard.tsx:376:16" data-component-path="src/app/ai-quote/wizard/components/QuoteWizard.tsx" data-component-line="376" data-component-file="QuoteWizard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D" onClick={commitAnswerAndNext} className={`btn-primary ${(!tempSingle || submitting) ? 'opacity-50 pointer-events-none' : ''}`}>{submitting ? '처리 중...' : '다음으로'}</button>
              </div>
            </div>
          )}
          {currentQuestion && !isTyping && currentQuestion.type !== 'single' && renderControls()}
        </div>
      </div>
    </div>
  );
}
